import React from "react";
import { Link } from "react-router-dom";
import { FiEye } from "react-icons/fi";

/* ----------------------------
   Image loader (Webpack safe)
----------------------------- */
let menuSmImages = {};
try {
  const req = require.context("../img/menu/sm", false, /\.(png|jpe?g|svg)$/);
  req.keys().forEach((k) => {
    menuSmImages[k.replace("./", "")] = req(k);
  });
} catch (err) {
  /* ignore */
}

/* ----------------------------
   Resolve Item Image
----------------------------- */
export const resolveItemImg = (img) => {
  if (!img) return "/img/placeholder.png";
  if (typeof img !== "string") img = String(img);
  if (img.startsWith("http") || img.startsWith("/")) return img;
  // every item and category image (incl. "menu/48/..." ids) has a 300px copy in menu/sm.
  // No dynamic require(`../img/${img}`) fallback: it makes webpack bundle all of src/img,
  // inlining ~110 unused 48px thumbs as base64 into the JS chunk.
  const filename = img.split("/").pop() || "";
  return menuSmImages[filename] || "/img/placeholder.png";
};

export const slugify = (s = "") =>
  String(s)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

/* ----------------------------
   Card helpers: kcal / protein badges + clean description
----------------------------- */
const getNutrient = (item, key, textPattern) => {
  const entry = Object.entries(item.nutrition || {}).find(([k]) => k.toLowerCase().includes(key));
  const raw = entry ? String(entry[1]) : (String(item.description || "").match(textPattern) || [])[1];
  const num = parseFloat(String(raw || "").replace(/\s+/g, ""));
  return Number.isFinite(num) ? Math.round(num) : null;
};
export const getKcal = (item) => getNutrient(item, "energy", /energy\s*[-:]?\s*([\d.\s]+)\s*kcal/i);
export const getProtein = (item) => getNutrient(item, "protein", /protein\s*[-:]?\s*([\d.\s]+)\s*g/i);
// descriptions carry a "(energy -516 kcal, ...)" suffix; the badge already shows it
export const cleanDescription = (s = "") => s.replace(/\(\s*energy[^)]*\)?/i, "").replace(/\s+/g, " ").trim();

/* ----------------------------
   Round category bubble (image + label)
----------------------------- */
export const CategoryBubble = ({ cat, active, onClick, ...rest }) => (
  <button type="button" onClick={onClick} className="flex w-16 flex-none flex-col items-center gap-1.5 md:w-20" {...rest}>
    <span
      className={`flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-white p-0.5 transition md:h-16 md:w-16 ${active ? "ring-2 ring-green-500 ring-offset-2 ring-offset-[#FAF8F5]" : "ring-1 ring-stone-200 hover:ring-green-300"}`}
    >
      <img
        src={resolveItemImg(cat.image48Id || cat.image || "")}
        alt=""
        loading="lazy"
        className="h-full w-full rounded-full object-cover"
        onError={(e) => { e.currentTarget.src = "/img/placeholder.png"; }}
      />
    </span>
    <span className={`line-clamp-2 text-center text-[11px] leading-tight md:text-xs ${active ? "font-bold text-green-700" : "font-medium text-gray-600"}`}>
      {cat.name || "Unnamed"}
    </span>
  </button>
);

/* ----------------------------
   Item card (eatnubo-style: image on top, badge, price + view button)
----------------------------- */
const MenuCard = ({ item, category, activeKey }) => {
  const itemId = encodeURIComponent(item.id || slugify(item.name));
  const catId = encodeURIComponent(category?.id || slugify(category?.name || "all"));
  const kcal = getKcal(item);
  const nonVeg = item.type === "nonveg";
  return (
    <Link
      to={`/menu/${catId}/${itemId}`}
      state={{ fromMenu: true, activeKey: category?.id || activeKey }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white transition hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
        <img
          src={resolveItemImg(item.img || item.imgFile)}
          alt={item.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => { e.currentTarget.src = "/img/placeholder.png"; }}
        />
        {kcal && (
          <span className="absolute left-2 top-2 rounded-full bg-white/95 px-2 py-0.5 text-[11px] font-semibold text-gray-800 shadow-sm">
            {kcal} kcal
          </span>
        )}
        <span className="absolute right-2 top-2 rounded-md bg-white/95 p-1 shadow-sm">
          <img src={nonVeg ? "/img/non-veg.svg" : "/img/veg.svg"} alt={nonVeg ? "Non-veg" : "Veg"} className="h-4 w-4" />
        </span>
      </div>

      <div className="flex flex-1 flex-col p-3 md:p-4">
        <h4 className="line-clamp-1 text-sm font-bold text-gray-900 md:text-base">{item.name}</h4>
        <p className="mt-1 line-clamp-1 text-xs text-gray-500 md:text-sm">{cleanDescription(item.description)}</p>
        <div className="mt-auto flex items-center justify-between pt-3">
          <span className="text-base font-bold text-gray-900">₹{item.price || "—"}</span>
          <span
            title="View details"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-white transition-colors group-hover:bg-green-600"
          >
            <FiEye className="h-4 w-4" aria-label="View details" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default MenuCard;
